import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { AlertCircle, CheckCircle2, Loader2, Send, Users } from 'lucide-react';
import { pushNotificationService, type UserWithActiveFCM, type SendNotificationPayload } from '@/services/push-notification.service';

const AdminPushNotifications = () => {
  const { toast } = useToast();
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('');
  const [filterCity, setFilterCity] = useState<string>('');
  const [filterState, setFilterState] = useState<string>('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [path, setPath] = useState('');
  const [responseMessage, setResponseMessage] = useState<string>('');
  const [responseStatus, setResponseStatus] = useState<'success' | 'error' | null>(null);

  // Fetch users with active FCM
  const { data: usersData, isLoading, error } = useQuery({
    queryKey: ['users-with-active-fcm'],
    queryFn: async () => {
      return await pushNotificationService.getUsersWithActiveFCM();
    },
  });

  const users = usersData?.data || [];

  // Get unique values for filters
  const uniqueRoles = useMemo(() => {
    return Array.from(new Set(users.map(u => u.user_role)));
  }, [users]);

  const uniqueCities = useMemo(() => {
    return Array.from(new Set(users.map(u => u.user_cidade)));
  }, [users]);

  const uniqueStates = useMemo(() => {
    return Array.from(new Set(users.map(u => u.user_estado)));
  }, [users]);

  // Filter users based on search and filters
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesSearch = !searchTerm || 
        user.user_codigo.toString().includes(searchTerm) ||
        user.user_nome_razao_social.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesRole = !filterRole || user.user_role === filterRole;
      const matchesCity = !filterCity || user.user_cidade === filterCity;
      const matchesState = !filterState || user.user_estado === filterState;

      return matchesSearch && matchesRole && matchesCity && matchesState;
    });
  }, [users, searchTerm, filterRole, filterCity, filterState]);

  // Send notification mutation
  const sendNotificationMutation = useMutation({
    mutationFn: async (payload: SendNotificationPayload) => {
      return await pushNotificationService.sendNotification(payload);
    },
    onSuccess: (data) => {
      setResponseMessage(data.message);
      setResponseStatus('success');
      setSelectedUsers([]);
      setTitle('');
      setMessage('');
      setPath('');
      toast({
        title: 'Sucesso',
        description: data.message,
      });
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : 'Erro ao enviar notificação';
      setResponseMessage(errorMessage);
      setResponseStatus('error');
      toast({
        title: 'Erro',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const handleSendNotification = () => {
    if (selectedUsers.length === 0) {
      toast({
        title: 'Aviso',
        description: 'Selecione pelo menos um usuário',
        variant: 'destructive',
      });
      return;
    }

    if (!title.trim()) {
      toast({
        title: 'Aviso',
        description: 'Informe o título da notificação',
        variant: 'destructive',
      });
      return;
    }

    if (!message.trim()) {
      toast({
        title: 'Aviso',
        description: 'Informe a mensagem da notificação',
        variant: 'destructive',
      });
      return;
    }

    const payload: SendNotificationPayload = {
      userIds: selectedUsers,
      title: title.trim(),
      message: message.trim(),
    };

    if (path.trim()) {
      payload.path = path.trim();
    }

    sendNotificationMutation.mutate(payload);
  };

  const toggleUserSelection = (userId: number) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.user_id));
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Send className="h-8 w-8 text-blue-600" />
        <div>
          <h1 className="text-3xl font-bold">Disparar Push Notifications</h1>
          <p className="text-gray-600">Envie notificações para usuários com FCM ativo</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left side - User selection */}
        <div className="lg:col-span-2 space-y-6">
          {/* Search and Filters */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Selecionar Usuários
              </CardTitle>
              <CardDescription>
                {filteredUsers.length} usuário(s) disponível(is)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                placeholder="Buscar por código ou nome..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full"
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Select value={filterRole} onValueChange={setFilterRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filtrar por role" />
                  </SelectTrigger>
                  <SelectContent>
                    {uniqueRoles.map(role => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterCity} onValueChange={setFilterCity}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filtrar por cidade" />
                  </SelectTrigger>
                  <SelectContent>
                    {uniqueCities.map(city => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterState} onValueChange={setFilterState}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filtrar por estado" />
                  </SelectTrigger>
                  <SelectContent>
                    {uniqueStates.map(state => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Users List */}
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">Carregando usuários...</span>
                </div>
              ) : error ? (
                <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  <AlertCircle className="h-5 w-5" />
                  Erro ao carregar usuários
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-8 text-gray-600">
                  Nenhum usuário encontrado com os filtros selecionados
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Select All checkbox */}
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <Checkbox
                      id="select-all"
                      checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                    <label
                      htmlFor="select-all"
                      className="text-sm font-medium cursor-pointer flex-1"
                    >
                      Selecionar todos ({filteredUsers.length})
                    </label>
                  </div>

                  {/* User items */}
                  <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                    {filteredUsers.map(user => (
                      <div
                        key={user.user_id}
                        className="flex items-center gap-3 p-3 border-b border-gray-200 hover:bg-gray-50 last:border-b-0"
                      >
                        <Checkbox
                          id={`user-${user.user_id}`}
                          checked={selectedUsers.includes(user.user_id)}
                          onCheckedChange={() => toggleUserSelection(user.user_id)}
                        />
                        <label
                          htmlFor={`user-${user.user_id}`}
                          className="flex-1 cursor-pointer"
                        >
                          <div className="text-sm font-medium">{user.user_nome_razao_social}</div>
                          <div className="text-xs text-gray-600">
                            [{user.user_codigo}] - {user.user_role} - {user.user_cidade}, {user.user_estado}
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right side - Notification form */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Detalhes da Notificação</CardTitle>
              <CardDescription>
                {selectedUsers.length} usuário(s) selecionado(s)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">Título *</label>
                <Input
                  placeholder="Ex: Super Live de Carnaval"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Mensagem *</label>
                <textarea
                  placeholder="Ex: Lorem ipsum dolor sit amet"
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows={4}
                />
              </div>

              <div>
                <label className="text-sm font-medium">Caminho (opcional)</label>
                <Input
                  placeholder="Ex: /loja-recompensas"
                  value={path}
                  onChange={e => setPath(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-gray-600 mt-2">
                  💡 O caminho é a rota do app após "/v2/" na URL. Deixe em branco para não adicionar navegação.
                </p>
              </div>

              <Button
                onClick={handleSendNotification}
                disabled={sendNotificationMutation.isPending || selectedUsers.length === 0}
                className="w-full"
                size="lg"
              >
                {sendNotificationMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {sendNotificationMutation.isPending ? 'Enviando...' : 'Enviar Notificação'}
              </Button>
            </CardContent>
          </Card>

          {/* Response message */}
          {responseMessage && (
            <Card
              className={
                responseStatus === 'success'
                  ? 'border-green-200 bg-green-50'
                  : 'border-red-200 bg-red-50'
              }
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  {responseStatus === 'success' ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                  )}
                  <div>
                    <p
                      className={
                        responseStatus === 'success'
                          ? 'text-green-800'
                          : 'text-red-800'
                      }
                    >
                      {responseMessage}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPushNotifications;
